import React, { useEffect, useState } from "react";
import api from "../api/axios";
import { FaPlus } from "react-icons/fa";
import { Loader2 } from "lucide-react";
import AddContactModal from "../components/AddContactModal";
import EditCustomerModal from "../components/EditCustomerModal";
import CustomerDetailsPanel from "./CustomerDetailsPanel";
import UiText from "../components/UiText";
import { useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/ui/ToastContext";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import useDebounce from "../utils/useDebounce";

const getStatusColor = (status) => {
  const normalized = (status || "active").toLowerCase();
  switch (normalized) {
    case "active":
      return "#10b981"; // green
    case "qualified":
      return "#f59e0b"; // orange
    case "inactive":
      return "#ef4444"; // red
    default:
      return "#6b7280"; // gray
  }
};

const capitalizeStatus = (status) => {
  if (!status) return "Active";
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [selectedContact, setSelectedContact] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deletingCustomer, setDeletingCustomer] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  const location = useLocation();
  const { user } = useAuth();
  const toast = useToast();

  // Server-side search/filter/pagination: only the current page of matching
  // customers is ever fetched, instead of the entire collection.
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/customers/", {
        params: {
          skip: (currentPage - 1) * itemsPerPage,
          limit: itemsPerPage,
          search: debouncedSearch || undefined,
          status_filter: activeTab !== "all" ? activeTab : undefined,
          company_id: companyFilter !== "all" ? companyFilter : undefined,
        },
      });
      setCustomers(res.data);
      const total = Number(res.headers?.["x-total-count"]);
      setTotalCount(Number.isFinite(total) ? total : res.data.length);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch customers.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      // Higher limit here: this call only populates the "Company" filter
      // dropdown, not the main table, so it needs the full company list.
      const res = await api.get("/api/companies/", { params: { limit: 500 } });
      setCompanies(res.data);
    } catch (err) {
      console.error("Failed to fetch companies", err);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, itemsPerPage, debouncedSearch, activeTab, companyFilter]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    const incomingCompanyId = location?.state?.companyId;
    if (incomingCompanyId) {
      setCompanyFilter(String(incomingCompanyId));
      setSelectedContact(null);
    }
  }, [location?.state?.companyId]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, activeTab, companyFilter]);

  const openAddModal = () => setShowAddModal(true);
  const closeAddModal = () => setShowAddModal(false);
  const handleSaved = async () => {
    await fetchCustomers();
    setShowAddModal(false);
    toast.success("Contact added successfully.");
  };

  const canEditCustomer = (customer) => {
    if (!user) return false;
    // Only admin has global edit/delete; executives can edit/delete only their own records
    return user.role === 'admin' || customer.created_by === user.id;
  };

  const handleEditClick = (customer) => {
    if (canEditCustomer(customer)) {
      setEditingCustomer(customer);
      setShowEditModal(true);
    }
  };

  const handleDeleteClick = async (customer) => {
    if (!canEditCustomer(customer)) {
      toast.error("You don't have permission to delete this customer");
      return;
    }

    setCustomerToDelete(customer);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!customerToDelete || deletingCustomer) return;

    setDeletingCustomer(true);
    try {
      await api.delete(`/api/customers/${customerToDelete.id}`);
      await fetchCustomers();
      if (selectedContact?.id === customerToDelete.id) {
        setSelectedContact(null);
      }
      setShowDeleteConfirm(false);
      toast.success(`"${customerToDelete.name}" was deleted.`);
      setCustomerToDelete(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete customer");
    } finally {
      setDeletingCustomer(false);
    }
  };

  const closeEditModal = () => setShowEditModal(false);
  const handleEditSaved = async () => {
    await fetchCustomers();
    setShowEditModal(false);
    setSelectedContact(null);
  };

  return (
    <div style={{ padding: "2rem" }}>
      {/* HEADER ROW */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "1.5rem",
        }}
      >
        <div>
          <h1 style={{ fontSize: "2rem", fontWeight: "700", margin: 0 }}>Contacts</h1>
          <p style={{ color: "#6b7280", marginTop: "4px" }}>Manage your contact relationships</p>
        </div>

        {user?.role !== 'user' && (
        <button
          onClick={openAddModal}
          style={{
            background: "#2563eb",
            color: "white",
            padding: "10px 18px",
            borderRadius: "8px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            border: "none",
            cursor: "pointer",
            height: "44px",
          }}
        >
          <FaPlus style={{ fontSize: "12px", verticalAlign: "middle" }} /> Add Contact
        </button>
        )}
      </div>

      {/* SEARCH + FILTERS */}
      <div className="bg-white p-4 rounded-xl shadow-sm mb-8">
        <div className="flex gap-4 items-center">
          <div className="flex items-center w-full">
            <input
              type="text"
              placeholder="Search contacts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 border border-gray-300 rounded-xl bg-white cursor-text text-gray-700 shadow-sm"
            />
          </div>

          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-xl bg-white text-gray-700 shadow-sm cursor-pointer"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="qualified">Qualified</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-xl bg-white text-gray-700 shadow-sm cursor-pointer"
          >
            <option value="all">All Companies</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* GRID LAYOUT */}
      <div className="grid grid-cols-3 gap-6">
        {/* LEFT: CONTACT TABLE */}
        <div className="col-span-2 bg-white rounded-xl shadow-sm p-6">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="pb-3 pt-2">
                  <UiText variant="section" as="span">Name</UiText>
                </th>
                <th className="pb-3 pt-2">
                  <UiText variant="section" as="span">Position</UiText>
                </th>
                <th className="pb-3 pt-2">
                  <UiText variant="section" as="span">Company</UiText>
                </th>
                <th className="pb-3 pt-2">
                  <UiText variant="section" as="span">Status</UiText>
                </th>
              </tr>
            </thead>

           
            <tbody>
          {loading ? (
            <tr>
              <td colSpan="4" style={{ padding: "2rem", textAlign: "center" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem" }}>
                  <Loader2 size={32} color="#3b82f6" style={{ animation: "spin 1s linear infinite" }} />
                  <span style={{ color: "#64748b", fontSize: "0.95rem" }}>Loading contacts...</span>
                </div>
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              </td>
            </tr>
          ) : error ? (
            <tr>
              <td colSpan="4" style={{ padding: "2rem", textAlign: "center", color: "red" }}>{error}</td>
            </tr>
          ) : customers.length === 0 ? (
            <tr>
              <td colSpan="4" style={{ padding: "2rem", textAlign: "center", color: "#9ca3af" }}>
                {user?.role === 'user' ? (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem", color: "#9ca3af" }}>
                    <div style={{ fontSize: "1rem", fontWeight: 600 }}>You have not been collabrated yet</div>
                    <div style={{ fontSize: "0.95rem" }}>Kindly contact Administrator</div>
                  </div>
                ) : (
                  <div>No contacts found.</div>
                )}
              </td>
            </tr>
          ) : (
            customers.map((c) => (
              <tr
                key={c.id}
                onClick={() => setSelectedContact(c)}
                className={`border-t cursor-pointer transition-all hover:bg-gray-50 ${
                  selectedContact?.id === c.id ? "bg-blue-50 ring-2 ring-inset ring-blue-400" : ""
                }`}
              >
                <td className="py-4">
                  <UiText variant="section" as="span" className="font-semibold text-gray-900">{c.name}</UiText>
                </td>
                <td className="py-4">
                  <UiText variant="small" as="span">{c.position || "-"}</UiText>
                </td>
                <td className="py-4">
                  <UiText variant="small" as="span">{c.company_name || "-"}</UiText>
                </td>
                <td className="py-4">
                  <span
                    style={{
                      background: getStatusColor(c.status),
                      color: "white",
                      fontSize: "9px",
                      fontWeight: "600",
                      padding: "3px 8px",
                      borderRadius: "9999px",
                      textTransform: "uppercase",
                      letterSpacing: "0.3px",
                      display: "inline-block"
                    }}
                  >
                    {capitalizeStatus(c.status)}
                  </span>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

        {/* PAGINATION CONTROLS */}
        {totalCount > 0 && (
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "1.5rem",
            paddingTop: "1rem",
            borderTop: "1px solid #e5e7eb",
          }}>
            {/* Left: Items per page */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <UiText variant="small" as="span">Show:</UiText>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  padding: "0.4rem 0.6rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "13px",
                  cursor: "pointer",
                  background: "#fff",
                }}
              >
                <option value={6}>6</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <UiText variant="small" as="span" style={{ color: "#6b7280" }}>
                of {totalCount} contacts
              </UiText>
            </div>

            {/* Right: Page navigation */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: "0.4rem 0.8rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  background: currentPage === 1 ? "#f3f4f6" : "#fff",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  fontSize: "13px",
                  fontWeight: "500",
                  color: currentPage === 1 ? "#9ca3af" : "#374151",
                }}
              >
                Previous
              </button>

              <div style={{ display: "flex", gap: "0.25rem" }}>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        padding: "0.4rem 0.7rem",
                        border: currentPage === pageNum ? "2px solid #2563eb" : "1px solid #d1d5db",
                        borderRadius: "6px",
                        background: currentPage === pageNum ? "#eff6ff" : "#fff",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: currentPage === pageNum ? "600" : "500",
                        color: currentPage === pageNum ? "#2563eb" : "#374151",
                        minWidth: "32px",
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: "0.4rem 0.8rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  background: currentPage === totalPages ? "#f3f4f6" : "#fff",
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  fontSize: "13px",
                  fontWeight: "500",
                  color: currentPage === totalPages ? "#9ca3af" : "#374151",
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

        {/* RIGHT: CONTACT DETAILS PANEL - UPDATED */}
        <div
          className="bg-white rounded-xl shadow-sm p-4 col-span-1"
          style={{
            position: "sticky",
            top: "2rem",
          }}
        >
          <CustomerDetailsPanel
            customer={selectedContact}
            onEdit={(contact) => handleEditClick(contact)}
            onDelete={(contact) => handleDeleteClick(contact)}
          />
        </div>
      </div>

      {showAddModal && <AddContactModal onClose={closeAddModal} onSuccess={handleSaved} />}
      {showEditModal && editingCustomer && (
        <EditCustomerModal
          customer={editingCustomer}
          companies={companies}
          onClose={closeEditModal}
          onSaved={handleEditSaved}
        />
      )}

      {showDeleteConfirm ? (
        <div className="confirm-overlay" role="dialog" aria-modal="true">
          <div className="confirm-modal">
            <div className="confirm-title">Confirm Delete</div>
            <div className="confirm-text">
              Are you sure you want to delete <strong>{customerToDelete?.name || "this contact"}</strong>? This action cannot be undone.
            </div>

            <div className="confirm-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setCustomerToDelete(null);
                }}
                disabled={deletingCustomer}
              >
                Cancel
              </button>

              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                disabled={deletingCustomer}
              >
                {deletingCustomer ? "Deleting..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

    </div>
  );
}
